import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Megaphone, Send, Eye, Users, MessageCircle, History, Sparkles, ShieldCheck,
  UserCog, RotateCcw, Loader2, Check, MapPin, ExternalLink, CheckCircle2, AlertTriangle,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Card, Btn, Input, Badge, TabBar } from "@/components/kit";

const KINDS = [
  { v: "announcement", l: "Comunicado" },
  { v: "policy", l: "Atualização/Política" },
  { v: "kudos", l: "Reconhecimento" },
];
const ROLES = [{ v: "rep", l: "Representante" }, { v: "manager", l: "Gestor" }, { v: "owner", l: "Diretor" }, { v: "admin", l: "Admin" }];

// data + hora da interação (fuso de Brasília) — ex.: "29/09/2026 · 14:32"
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  }).replace(",", " ·");
};

/* ---------------- Balão de localização (check-in de presença) ---------------- */
const fmtDist = (km) => km == null ? "" : (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`);

function LocationBubble({ loc, at, idx }) {
  const st = loc.status;
  const badge = st === "near"
    ? { tone: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: CheckCircle2,
        label: loc.account_name ? `Perto de ${loc.account_name} · ${fmtDist(loc.distance_km)}` : "Presença confirmada" }
    : st === "far"
    ? { tone: "bg-amber-50 text-amber-700 border-amber-200", Icon: AlertTriangle,
        label: `A ${fmtDist(loc.distance_km)} da referência${loc.account_name ? ` de ${loc.account_name}` : ""} · divergência` }
    : st === "baseline"
    ? { tone: "bg-sky-50 text-sky-700 border-sky-200", Icon: MapPin,
        label: `Base de referência criada${loc.account_name ? ` p/ ${loc.account_name}` : ""}` }
    : { tone: "bg-zinc-100 text-zinc-600 border-zinc-200", Icon: MapPin, label: "Local registrado" };
  return (
    <div className="w-[80%] overflow-hidden rounded-2xl rounded-bl-sm border border-zinc-200 bg-white" data-testid={`conv-location-${idx}`}>
      <a href={loc.maps_link} target="_blank" rel="noreferrer" className="block" title="Abrir no Google Maps">
        <iframe src={loc.map_url} title="Mapa da localização" loading="lazy"
                className="pointer-events-none h-32 w-full border-0" data-testid={`conv-location-map-${idx}`} />
      </a>
      <div className="space-y-2 p-3">
        <div className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-1 text-[11px] font-medium ${badge.tone}`} data-testid={`conv-location-badge-${idx}`}>
          <badge.Icon size={13} /> {badge.label}
        </div>
        {loc.address && <p className="text-xs text-zinc-500">{loc.address}</p>}
        <a href={loc.maps_link} target="_blank" rel="noreferrer"
           className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline" data-testid={`conv-location-link-${idx}`}>
          <ExternalLink size={12} /> Abrir no Google Maps
        </a>
        {at && <p className="text-right text-[10px] text-zinc-400">{fmtDateTime(at)}</p>}
      </div>
    </div>
  );
}

/* ---------------- Disparo em massa ---------------- */
function Disparo({ emps }) {
  const [kind, setKind] = useState("announcement");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [atype, setAtype] = useState("all");
  const [targets, setTargets] = useState([]);
  const [preview, setPreview] = useState(null);
  const [recips, setRecips] = useState(null);
  const [busy, setBusy] = useState(false);

  const departments = useMemo(() => [...new Set(emps.map((e) => e.department).filter(Boolean))], [emps]);
  const toggle = (v) => setTargets((t) => (t.includes(v) ? t.filter((x) => x !== v) : [...t, v]));
  useEffect(() => { setTargets([]); }, [atype]);

  const audience = () => ({ type: atype, targets });

  const doPreview = async () => {
    if (!message.trim()) { toast.error("Escreva a mensagem primeiro"); return; }
    setBusy(true);
    try {
      const [r1, r2] = await Promise.all([
        api.post("/campaigns/render", { kind, message }),
        api.post("/campaigns/preview", { audience: audience() }),
      ]);
      setPreview(r1.data.preview); setRecips(r2.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const send = async () => {
    if (!preview) { toast.error("Gere a pré-visualização antes de disparar"); return; }
    if (!recips?.count) { toast.error("Nenhum destinatário"); return; }
    if (!window.confirm(`Confirmar disparo para ${recips.count} pessoa(s)?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/campaigns", { title, kind, message, audience: audience() });
      toast.success(`Campanha enviada para ${data.sent_count} pessoa(s)`);
      setMessage(""); setTitle(""); setPreview(null); setRecips(null);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="p-6">
        <div className="mb-4 flex items-center gap-2"><Megaphone size={18} className="text-indigo-600" /><h2 className="font-display font-semibold text-zinc-900">Nova campanha</h2></div>
        <div className="space-y-3">
          <div className="flex gap-2">
            {KINDS.map((k) => (
              <button key={k.v} data-testid={`kind-${k.v}`} onClick={() => setKind(k.v)}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${kind === k.v ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-zinc-200 text-zinc-500 hover:text-zinc-800"}`}>{k.l}</button>
            ))}
          </div>
          <Input data-testid="campaign-title" placeholder="Título interno (ex.: Reunião geral)" value={title} onChange={(e) => setTitle(e.target.value)} />
          <textarea data-testid="campaign-message" placeholder="Escreva a mensagem…" value={message} onChange={(e) => setMessage(e.target.value)}
            className="min-h-[120px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/15" />

          <div>
            <p className="mb-1 text-xs font-medium text-zinc-500">Público</p>
            <div className="flex flex-wrap gap-2">
              {[["all", "Todos"], ["department", "Por setor"], ["role", "Por cargo"], ["selected", "Selecionar pessoas"]].map(([v, l]) => (
                <button key={v} data-testid={`audience-${v}`} onClick={() => setAtype(v)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${atype === v ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 text-zinc-500 hover:text-zinc-800"}`}>{l}</button>
              ))}
            </div>
            {atype === "department" && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="target-departments">
                {departments.map((d) => (
                  <label key={d} className={`cursor-pointer rounded-md border px-2 py-1 text-xs ${targets.includes(d) ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-zinc-200 text-zinc-500"}`}>
                    <input type="checkbox" className="hidden" checked={targets.includes(d)} onChange={() => toggle(d)} />{d}
                  </label>
                ))}
              </div>
            )}
            {atype === "role" && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="target-roles">
                {ROLES.map((r) => (
                  <label key={r.v} className={`cursor-pointer rounded-md border px-2 py-1 text-xs ${targets.includes(r.v) ? "border-indigo-300 bg-indigo-50 text-indigo-700" : "border-zinc-200 text-zinc-500"}`}>
                    <input type="checkbox" className="hidden" checked={targets.includes(r.v)} onChange={() => toggle(r.v)} />{r.l}
                  </label>
                ))}
              </div>
            )}
            {atype === "selected" && (
              <div className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded-md border border-zinc-200 p-2" data-testid="target-selected">
                {emps.filter((e) => e.phone).map((e) => (
                  <label key={e.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-zinc-50">
                    <input type="checkbox" checked={targets.includes(e.id)} onChange={() => toggle(e.id)} /> {e.name} <span className="text-xs text-zinc-400">· {e.department}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2">
            <Btn variant="outline" onClick={doPreview} disabled={busy} data-testid="campaign-preview-btn"><Eye size={16} /> Pré-visualizar</Btn>
            <Btn onClick={send} disabled={busy || !preview} data-testid="campaign-send-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Disparar</Btn>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="mb-4 flex items-center gap-2"><Eye size={18} className="text-indigo-600" /><h2 className="font-display font-semibold text-zinc-900">Pré-visualização</h2></div>
        {!preview ? (
          <p className="text-sm text-zinc-400">Clique em “Pré-visualizar” para ver exatamente como a mensagem chega no WhatsApp e para quantas pessoas.</p>
        ) : (
          <div className="space-y-4" data-testid="campaign-preview">
            <div className="rounded-2xl rounded-bl-sm border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-800 whitespace-pre-wrap">{preview}</div>
            <div className="flex items-center gap-2 text-sm text-zinc-600" data-testid="recipient-count">
              <Users size={16} className="text-indigo-600" /> <b>{recips?.count}</b> destinatário(s)
              {recips?.departments?.length ? <span className="text-zinc-400">· {recips.departments.join(", ")}</span> : null}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {(recips?.recipients || []).slice(0, 30).map((r) => <Badge key={r.id}>{r.name}</Badge>)}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Conversa direta (via ISA ou takeover) ---------------- */
function Conversa({ emps }) {
  const withPhone = useMemo(() => emps.filter((e) => e.phone), [emps]);
  const [empId, setEmpId] = useState("");
  const [conv, setConv] = useState(null);
  const [instruction, setInstruction] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const threadRef = useRef(null);

  useEffect(() => { if (!empId && withPhone.length) setEmpId(withPhone[0].id); }, [withPhone, empId]);
  const load = () => empId ? api.get(`/conversations/${empId}`).then(({ data }) => setConv(data)).catch(() => {}) : Promise.resolve();
  const doRefresh = () => {
    if (!empId) return;
    setRefreshing(true);
    load().finally(() => setTimeout(() => setRefreshing(false), 500));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); setDraft(""); setInstruction(""); }, [empId]);
  // always keep the newest message in view — sem isso, mensagens novas entram no rodapé (fora da
  // tela) e parece que "nada acontece" ao atualizar.
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [conv]);
  // keep the thread live: poll every 6s and refetch when the window regains focus, so novas
  // mensagens (perguntas de agenda, respostas da ISA…) aparecem quase que imediatamente.
  useEffect(() => {
    if (!empId) return;
    const iv = setInterval(load, 6000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(iv); window.removeEventListener("focus", onFocus); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empId]);

  const isHuman = conv?.control?.mode === "human";

  const compose = async () => {
    if (!instruction.trim()) return;
    setBusy(true);
    try { const { data } = await api.post(`/conversations/${empId}/compose`, { instruction }); setDraft(data.preview); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const send = async () => {
    if (!draft.trim()) { toast.error("Nada para enviar — gere ou escreva a mensagem"); return; }
    setBusy(true);
    try {
      await api.post(`/conversations/${empId}/send`, { text: draft, via: isHuman ? "human" : "isa" });
      toast.success("Mensagem enviada"); setDraft(""); setInstruction(""); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const takeover = async () => { await api.post(`/conversations/${empId}/takeover`).catch((e) => toast.error(apiErr(e))); toast.success("Você assumiu a conversa — a Isa está em pausa"); load(); };
  const release = async () => { await api.post(`/conversations/${empId}/release`).catch((e) => toast.error(apiErr(e))); toast.success("Conversa devolvida para a Isa"); load(); };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="flex h-[460px] flex-col p-0">
        <div className="flex items-center justify-between border-b border-zinc-200 p-3">
          <select data-testid="conv-employee" value={empId} onChange={(e) => setEmpId(e.target.value)} className="rounded-md border border-input bg-white px-2 py-1.5 text-sm">
            {withPhone.map((e) => <option key={e.id} value={e.id}>{`${e.name} (${e.role})`}</option>)}
          </select>
          <div className="flex items-center gap-1">
            {isHuman
              ? <Badge tone="warn">Operador no controle</Badge>
              : <Badge tone="primary"><Sparkles size={11} className="mr-1" /> Isa ativa</Badge>}
            <button onClick={doRefresh} type="button" title="Atualizar conversa" data-testid="conv-refresh-btn"
                    className="ml-1 rounded-md p-1.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700 active:scale-90">
              <RotateCcw size={14} className={refreshing ? "animate-spin" : "transition-transform"} />
            </button>
          </div>
        </div>
        <div ref={threadRef} className="flex-1 space-y-2 overflow-y-auto bg-zinc-50 p-4" data-testid="conv-thread">
          {(conv?.thread || []).map((m, i) => (
            <div key={i} className={`flex ${m.who === "company" ? "justify-end" : "justify-start"}`}>
              {m.type === "location" && m.location ? (
                <LocationBubble loc={m.location} at={m.at} idx={i} />
              ) : (
              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.who === "company" ? "rounded-br-sm bg-zinc-900 text-white" : "rounded-bl-sm border border-zinc-200 bg-white text-zinc-800"}`}>
                <p className="whitespace-pre-wrap">{m.text}</p>
                {m.at && (
                  <p className={`mt-1 text-right text-[10px] ${m.who === "company" ? "text-white/50" : "text-zinc-400"}`} data-testid={`conv-msg-time-${i}`}>
                    {fmtDateTime(m.at)}
                  </p>
                )}
              </div>
              )}
            </div>
          ))}
          {!conv?.thread?.length && <p className="text-center text-sm text-zinc-400">Sem histórico ainda.</p>}
        </div>
      </Card>

      <Card className="p-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2"><MessageCircle size={18} className="text-indigo-600" /><h2 className="font-display font-semibold text-zinc-900">{isHuman ? "Você está no controle" : "Falar pela Isa"}</h2></div>
          {isHuman
            ? <Btn variant="outline" onClick={release} data-testid="release-btn"><RotateCcw size={15} /> Devolver p/ Isa</Btn>
            : <Btn variant="outline" onClick={takeover} data-testid="takeover-btn"><UserCog size={15} /> Assumir controle</Btn>}
        </div>

        {isHuman ? (
          <p className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700"><ShieldCheck size={13} className="mt-0.5" /> A Isa está pausada nesta conversa. Você escreve com suas próprias palavras — tudo continua auditado.</p>
        ) : (
          <>
            <p className="mb-2 text-xs text-zinc-500">Diga o que quer comunicar; a Isa escreve na voz dela. Você revisa a prévia antes de enviar.</p>
            <div className="flex gap-2">
              <Input data-testid="conv-instruction" placeholder="Ex.: avisa que a reunião mudou pras 15h" value={instruction} onChange={(e) => setInstruction(e.target.value)} onKeyDown={(e) => e.key === "Enter" && compose()} />
              <Btn variant="outline" onClick={compose} disabled={busy} data-testid="conv-compose-btn">{busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Gerar prévia</Btn>
            </div>
          </>
        )}

        <div className="mt-4">
          <p className="mb-1 text-xs font-medium text-zinc-500">{isHuman ? "Sua mensagem" : "Prévia (edite se quiser)"}</p>
          <textarea data-testid="conv-draft" value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder={isHuman ? "Escreva sua mensagem…" : "A prévia gerada pela Isa aparece aqui…"}
            className="min-h-[110px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/15" />
          <Btn onClick={send} disabled={busy || !draft.trim()} className="mt-2 w-full" data-testid="conv-send-btn"><Send size={16} /> Enviar no WhatsApp</Btn>
        </div>
      </Card>
    </div>
  );
}

/* ---------------- Histórico ---------------- */
function Historico() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/campaigns").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-3" data-testid="campaign-history">
      {rows.map((c) => (
        <Card key={c.id} className="p-4">
          <div className="flex items-center justify-between gap-2">
            <p className="font-medium text-zinc-900">{c.title}</p>
            <span className="flex items-center gap-2"><Badge tone="primary">{c.kind}</Badge><Badge tone="pos">{c.recipient_count} enviadas</Badge></span>
          </div>
          <p className="mt-2 whitespace-pre-wrap rounded-lg border border-zinc-200 bg-zinc-50 p-2 text-xs text-zinc-600">{c.rendered}</p>
          <p className="mt-2 text-xs text-zinc-400">por {c.sender_name} · {String(c.sent_at).slice(0, 16).replace("T", " ")}</p>
        </Card>
      ))}
      {!rows.length && <p className="text-sm text-zinc-500">Nenhuma campanha enviada ainda.</p>}
    </div>
  );
}

export default function Campaigns() {
  const [emps, setEmps] = useState([]);
  const [tab, setTab] = useState("disparo");
  useEffect(() => { api.get("/employees").then(({ data }) => setEmps(data)).catch(() => {}); }, []);
  const tabs = [
    { key: "disparo", label: "Disparo em massa", icon: Megaphone },
    { key: "conversa", label: "Conversa direta", icon: MessageCircle },
    { key: "historico", label: "Histórico", icon: History },
  ];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Campanhas</h1>
        <p className="mt-1 text-sm text-zinc-500">Comunicação interna: avise todo mundo, um setor ou uma pessoa — sempre com pré-visualização antes de enviar.</p>
      </div>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />
      {tab === "disparo" && <Disparo emps={emps} />}
      {tab === "conversa" && <Conversa emps={emps} />}
      {tab === "historico" && <Historico />}
    </div>
  );
}
